import React from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { useBooking } from "@/context/booking"
import { createBooking } from "@/lib/api"

type BookingFormProps = {
  children?: React.ReactNode
  onSubmit?: (payload: {
    location: "tan_dinh" | "thu_duc"
    guests: number
    date: string
    time: string
    name?: string
    phone?: string
  }) => void
}

const BookingForm: React.FC<BookingFormProps> = ({ children, onSubmit }) => {
  const { open, setOpen } = useBooking()
  const [location, setLocation] = React.useState<"tan_dinh" | "thu_duc">(
    "tan_dinh"
  )
  const [guests, setGuests] = React.useState(2)
  const [date, setDate] = React.useState("")
  const [time, setTime] = React.useState("")
  const [name, setName] = React.useState("")
  const [phone, setPhone] = React.useState("")
  const TODAY_VN = React.useMemo(() => {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Ho_Chi_Minh",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    return formatter.format(new Date())
  }, [])

  const NOW_TIME_VN = React.useMemo(() => {
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Ho_Chi_Minh",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    return formatter.format(new Date())
  }, [])

  const timeOptions = React.useMemo(() => {
    const [curH, curM] = NOW_TIME_VN.split(":").map(Number)
    const currentMinutes = curH * 60 + curM
    const startMinutes =
      date === TODAY_VN ? Math.ceil((currentMinutes + 1) / 30) * 30 : 9 * 60
    const endMinutes = 22 * 60 + 30 // 22:30
    const slots: string[] = []
    for (let m = Math.max(startMinutes, 9 * 60); m <= endMinutes; m += 30) {
      const hh = String(Math.floor(m / 60)).padStart(2, "0")
      const mm = String(m % 60).padStart(2, "0")
      slots.push(`${hh}:${mm}`)
    }
    return slots
  }, [date, NOW_TIME_VN, TODAY_VN])

  React.useEffect(() => {
    if (!date) {
      setDate(TODAY_VN)
    }
  }, [TODAY_VN, date])

  React.useEffect(() => {
    if (timeOptions.length && (!time || !timeOptions.includes(time))) {
      setTime(timeOptions[0])
    }
  }, [timeOptions])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSubmit?.({ location, guests, date, time, name, phone })

    const payload = {
      dia_diem: location,
      so_luong_khach: guests,
      ngay: date.split("-").reverse().join("-"), // yyyy-mm-dd -> dd-mm-yyyy
      thoi_gian: time,
      ten: name,
      so_dien_thoai: phone,
    }
    try {
      await createBooking(payload)
      setOpen(false)
    } catch (err) {
      console.error("Booking failed", err)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children ? <DialogTrigger asChild>{children}</DialogTrigger> : null}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Đặt bàn</DialogTitle>
          <DialogDescription>Nhập thông tin đặt bàn của bạn</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-2">
            <label htmlFor="location" className="text-sm font-medium">
              Địa điểm
            </label>
            <select
              id="location"
              value={location}
              onChange={e =>
                setLocation(e.target.value as "tan_dinh" | "thu_duc")
              }
              className="h-10 w-full rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-primary bg-white"
              required
            >
              <option value="tan_dinh">Nhà hàng Tân Định</option>
              <option value="thu_duc">Nhà hàng Thủ Đức</option>
            </select>
          </div>
          <div className="grid gap-2">
            <label htmlFor="guests" className="text-sm font-medium">
              Số lượng khách
            </label>
            <input
              id="guests"
              type="number"
              min={1}
              value={guests}
              onChange={e => setGuests(Number(e.target.value))}
              className="h-10 w-full rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <label htmlFor="date" className="text-sm font-medium">
                Ngày
              </label>
              <input
                id="date"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                min={TODAY_VN}
                className="h-10 w-full rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            <div className="grid gap-2">
              <label htmlFor="time" className="text-sm font-medium">
                Thời gian
              </label>
              <select
                id="time"
                value={time}
                onChange={e => setTime(e.target.value)}
                className="h-10 w-full rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                required
              >
                {timeOptions.map(t => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
                {!timeOptions.length && (
                  <option value="" disabled>
                    Không còn khung giờ phù hợp hôm nay
                  </option>
                )}
              </select>
            </div>
          </div>

          <div className="grid gap-2">
            <label htmlFor="name" className="text-sm font-medium">
              Tên
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Nhập tên của bạn"
              className="h-10 w-full rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div className="grid gap-2">
            <label htmlFor="phone" className="text-sm font-medium">
              Số điện thoại
            </label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="VD: 0912 345 678"
              pattern="^[0-9\s+()-]{8,}$"
              className="h-10 w-full rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Hủy
            </Button>
            <Button type="submit" className="text-white">
              Đặt bàn
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default BookingForm
